import { ChangeCartPayload } from '@/@types/shopify';
import { createLogger } from '@/lib/logger';
import { Cart, CartEvents, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { type MiniCart } from './mini-cart';

/**
 * Mini-cart header web component.
 *
 * Responsibilities:
 * - Shows the current cart item count.
 * - Emits UI update/close events when the close button is clicked.
 * - Subscribes to `cart:changed` to keep the header state in sync.
 *
 * This element expects to be a descendant of a `<mini-cart>` element and will
 * reuse the parent's `CartEventBus` instance for coordinated eventing.
 */
class MiniCartHeader extends HTMLElement {
  private cartCountElem: HTMLElement | null = null;
  private closeBtn: HTMLElement | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-header');

  /**
   * Create a MiniCartHeader.
   */
  constructor() {
    super();
  }

  /**
   * Emit UI close related events when the close button is clicked.
   * @param e Click event from the close button.
   */
  private emitClose(e: Event) {
    this.eventBus?.emit(CartEvents.UIUpdate, { action: 'close' });
    this.eventBus?.emit(CartEvents.UIClose);
  }

  /**
   * Handle `cart:changed` events by updating the header UI.
   * @param e CustomEvent carrying `{ cart }` in `detail`.
   */
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    this.logger.info('Changed event received', e);
    const { cart } = e.detail;
    this.updateUI(cart);
  }

  /**
   * Update the header UI based on the latest cart payload.
   * @param cart The latest cart.
   */
  private updateUI(cart: Cart) {
    if (this.cartCountElem) {
      this.logger.info('Updating cart count', cart.item_count);
      this.cartCountElem.textContent = cart.item_count.toString();
    }
  }

  /**
   * Bind DOM and bus event listeners required for the header interactions.
   */
  private bindEvents() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.emitClose.bind(this));
    }

    // Close minicart on backdrop click
    const backdrop = document.querySelector('mini-cart-backdrop') as HTMLElement;
    if (backdrop) {
      backdrop.addEventListener('click', this.emitClose.bind(this));
    }

    this.eventBus?.on(CartEvents.DebouncedChange, this.onCartChanged.bind(this));
  }

  /**
   * Remove all listeners bound in `bindEvents`.
   */
  private unbindEvents() {
    this.eventBus?.off(CartEvents.DebouncedChange, this.onCartChanged.bind(this));

    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.emitClose.bind(this));
    }
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();
  }

  /**
   * Lifecycle hook: invoked when the element is added to the document.
   *
   * - Binds references to UI elements.
   * - Acquires the parent `<mini-cart>`'s event bus.
   * - Wires the close button to emit `mini-cart` UI events.
   * - Subscribes to `cart:changed` to update the item count.
   */
  connectedCallback() {
    this.cartCountElem = this.querySelector('[data-item-count]') as HTMLElement | null;
    this.closeBtn = this.querySelector('[data-close-mini-cart]') as HTMLElement | null;

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartHeader must be a child of a MiniCart element');
      throw new Error('MiniCartHeader must be a child of a MiniCart element');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));
  }

  /**
   * Lifecycle hook: invoked when the element is removed from the document.
   * Cleans up event listeners to avoid leaks.
   */
  disconnectedCallback() {
    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-header')) {
  customElements.define('mini-cart-header', MiniCartHeader);
}
